import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { auth, fmtAmt, lbl, inp, card } from '../RealTradePage';
import AptBucketVolume from './AptBucketVolume';   // [RT_APTVOL_V1] 평형별 거래건수

// ─────────────────────────────────────────────
// 단지분석 탭
//   /api/real-trades/apt-detail  → 세대수·좌표·매매율
//   /api/real-trades/apt-trend   → 면적 타입별 가격 추이
//   /api/real-trades/agents-near → 반경 내 중개사무소 (데이터 준비되면)
//
// [단지 식별]
//   apt_nm 만으로는 단지를 특정할 수 없다("신동아"가 여러 곳).
//   반드시 (lawd_cd, umd_nm, jibun) 조합으로 조회한다.
// ─────────────────────────────────────────────

const COLORS = ['#3b82f6','#ef4444','#10b981','#f59e0b',
                '#8b5cf6','#ec4899','#14b8a6','#f97316'];

export default function AptTrend({ lawdCd, zone, from, to, propType, pickedApt }) {
  const [aptList, setAptList] = useState([]);
  const [keyword, setKeyword] = useState('');
  const [sel, setSel] = useState(null);       // 선택된 단지 (마스터 행)
  const [detail, setDetail] = useState(null); // apt-detail 결과
  const [series, setSeries] = useState([]);
  const [agents, setAgents] = useState(null); // 반경 내 중개사무소
  const [loading, setLoading] = useState(false);
  const [metric, setMetric] = useState('avg');
  // 선택 단지의 개별 거래내역 (기간 내 전체)
  const [deals, setDeals] = useState([]);
  const [dealArea, setDealArea] = useState('');   // 평형(area_type) 필터
  // 주변 중개사무소 표시 on/off
  const [showAgents, setShowAgents] = useState(false);

  // 카카오맵 인스턴스 보관 (리렌더링 때 재생성 방지)
  const mapDivRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);

  // ── 단지 목록: rt_apt_master 기반(거래량 상위) ──
  useEffect(() => {
    if (!lawdCd) return;
    // top=500 : 단지분석은 전체 단지를 다뤄야 하므로 넉넉히 받는다
      axios.get(`/api/real-trades/summary?${(() => {
        const q = new URLSearchParams({ from, to, top: 500, prop_type: propType });
        if (lawdCd) q.set('lawd_cd', lawdCd);
        else if (zone) q.set('zone', zone);
        return q.toString();
      })()}`, auth())
      .then(r => setAptList(r.data.top_apt || []))
      .catch(() => setAptList([]));
    setSel(null); setDetail(null); setSeries([]); setAgents(null);
  }, [lawdCd, zone, from, to, propType]);

  // ── 단지 선택 시: 상세 + 추이를 함께 조회 ──
  const loadApt = useCallback(async (aptNm, umdNm, jibunArg, lawdArg) => {
    const LC = lawdArg || lawdCd;
    setLoading(true);
    setAgents(null);
    try {
      // 1) 상세 먼저 (지번을 알아내야 추이를 정확히 조회할 수 있다)
      const dq = new URLSearchParams({ lawd_cd: LC, apt_nm: aptNm, from, to, prop_type: propType });
      const dRes = await axios.get(`/api/real-trades/apt-detail?${dq}`, auth());
      let items = dRes.data.items || [];

      // 같은 이름이 여러 곳이면 법정동으로 좁힌다
      // 지번이 있으면 그것으로 정확히 특정한다 (동명이단지 구분)
      if (jibunArg && items.length > 1) {
        const f = items.filter(i => i.jibun === jibunArg);
        if (f.length) items = f;
      } else if (umdNm && items.length > 1) {
        const f = items.filter(i => i.umd_nm === umdNm);
        if (f.length) items = f;
      }
      const d = items[0] || null;
      setDetail(d);
      setSel({ apt_nm: aptNm, umd_nm: umdNm, jibun: jibunArg });

      // 2) 추이 - 지번이 있으면 정확히, 없으면 이름으로
      const tq = new URLSearchParams({ lawd_cd: LC, from, to, prop_type: propType });
      if (d?.umd_nm && d?.jibun) {
        tq.set('umd_nm', d.umd_nm); tq.set('jibun', d.jibun);
      } else {
        tq.set('apt_nm', aptNm);
      }
      const tRes = await axios.get(`/api/real-trades/apt-trend?${tq}`, auth());
      setSeries(tRes.data.series || []);

      // 개별 거래내역도 함께 조회한다.
      // 그래프는 월평균이라 실제 거래 하나하나를 보려면 목록이 필요하다.
      const lq = new URLSearchParams({
        lawd_cd: LC, from, to, prop_type: propType,
        size: 500, sort: 'date_desc',
      });
      if (d?.umd_nm && d?.jibun) {
        lq.set('umd_nm', d.umd_nm);
      }
      lq.set('apt_nm', aptNm);
      const lRes = await axios.get(`/api/real-trades/list?${lq}`, auth());
      // 지번까지 일치하는 것만 남긴다 (동명이단지 제외)
      const all = lRes.data.rows || [];
      setDeals(d?.jibun ? all.filter(x => x.jibun === d.jibun) : all);
      setDealArea('');
    } catch (e) {
      alert('조회 실패: ' + (e.response?.data?.error || e.message));
    } finally {
      setLoading(false);
    }
  }, [lawdCd, zone, from, to, propType]);

  // 단지검색 탭에서 행을 클릭하면 pickedApt 가 내려온다.
  // 해당 단지를 자동으로 열어준다.
  useEffect(() => {
    if (pickedApt?.apt_nm) {
      loadApt(pickedApt.apt_nm, pickedApt.umd_nm, pickedApt.jibun);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickedApt]);

  // ── 카카오맵 렌더링 ──
  // detail 이 바뀔 때마다 지도를 갱신한다.
  // 지도 객체는 한 번만 만들고 이후엔 중심좌표와 마커만 교체한다.
  useEffect(() => {
    if (!detail?.lat || !detail?.lng) return;
    if (!window.kakao?.maps) return;

    const { kakao } = window;
    kakao.maps.load(() => {
      const center = new kakao.maps.LatLng(detail.lat, detail.lng);

      // 지도 div 가 화면에서 사라졌다가 다시 생기면(좌표 없는 단지를 거친 경우)
      // 기존 map 객체는 죽은 DOM 을 가리키게 된다.
      // → div 가 바뀌었는지 확인하고 바뀌었으면 새로 만든다.
      if (!mapRef.current || mapRef.current.__div !== mapDivRef.current) {
        mapRef.current = new kakao.maps.Map(mapDivRef.current, { center, level: 4 });
        mapRef.current.__div = mapDivRef.current;   // 어느 div 로 만들었는지 기록
        markersRef.current = [];                     // 마커 참조도 초기화
      } else {
        mapRef.current.setCenter(center);
        mapRef.current.relayout();                   // 숨겨졌다 나타난 경우 크기 재계산
      }

      // 기존 마커 제거
      markersRef.current.forEach(m => m.setMap(null));
      markersRef.current = [];

      // 단지 마커 (기본 마커)
      const aptMarker = new kakao.maps.Marker({ position: center, map: mapRef.current });
      markersRef.current.push(aptMarker);

      const iw = new kakao.maps.InfoWindow({
        content: `<div style="padding:6px 10px;font-size:12px;white-space:nowrap">
                    <b>${detail.apt_nm}</b><br/>${detail.hhld_cnt || '?'}세대</div>`,
      });
      iw.open(mapRef.current, aptMarker);

      // 중개사무소 마커 (있을 때만)
      if (agents?.rows?.length) {
        agents.rows.forEach(a => {
          const mk = new kakao.maps.Marker({
            position: new kakao.maps.LatLng(a.lat, a.lng),
            map: mapRef.current,
            title: a.bsnm_cmpnm,
          });
          markersRef.current.push(mk);
        });

        // 반경 원 표시
        const circle = new kakao.maps.Circle({
          center, radius: agents.radius,
          strokeWeight: 2, strokeColor: '#3b82f6', strokeOpacity: 0.6,
          fillColor: '#3b82f6', fillOpacity: 0.06,
        });
        circle.setMap(mapRef.current);
        markersRef.current.push(circle);
      }
    });
  }, [detail, agents]);

  // ── 반경 내 중개사무소 조회 ──
  const loadAgents = async () => {
    if (!detail?.lat) return;
    try {
      const q = new URLSearchParams({ lat: detail.lat, lng: detail.lng });
      const res = await axios.get(`/api/real-trades/agents-near?${q}`, auth());
      setAgents(res.data);
    } catch (e) {
      alert('중개사무소 조회 실패: ' + (e.response?.data?.error || e.message));
    }
  };

  // ── recharts 데이터 변환 ──
  const chartData = (() => {
    const ymSet = new Set();
    series.forEach(s => s.points.forEach(p => ymSet.add(p.ym)));
    return [...ymSet].sort().map(ym => {
      const row = { ym: `${ym.slice(2,4)}.${ym.slice(4,6)}` };
      series.forEach(s => {
        const p = s.points.find(x => x.ym === ym);
        if (p) row[`${s.area_type}㎡`] = metric === 'avg' ? p.avg : p.unit;
      });
      return row;
    });
  })();

  const filtered = keyword ? aptList.filter(a => a.apt_nm.includes(keyword)) : aptList;

  return (
    <div>
      <div style={{ ...card, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <label style={lbl}>단지 검색</label>
        <input value={keyword} onChange={e => setKeyword(e.target.value)}
               placeholder="단지명 일부" style={{ ...inp, width: 160 }} />
        <span style={{ fontSize: 12, color: '#94a3b8' }}>
          거래량 상위 {aptList.length}개
        </span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
          <button onClick={() => setMetric('avg')} style={metric==='avg'?tabOn:tabOff}>거래금액</button>
          <button onClick={() => setMetric('unit')} style={metric==='unit'?tabOn:tabOff}>평당가</button>
        </div>
      </div>

      {/* 단지 선택 */}
      <div style={{ ...card, maxHeight: 120, overflowY: 'auto',
                    display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {filtered.map((a, i) => {
          const on = sel?.apt_nm === a.apt_nm && sel?.jibun === a.jibun;
          return (
            <button key={i} onClick={() => loadApt(a.apt_nm, a.umd_nm, a.jibun, a.lawd_cd)}
              style={{ padding: '5px 10px', fontSize: 12, cursor: 'pointer', borderRadius: 4,
                border: '1px solid ' + (on ? '#3b82f6' : '#e2e8f0'),
                background: on ? '#dbeafe' : '#fff', fontWeight: on ? 600 : 400 }}>
              {a.apt_nm}
              <span style={{ color: '#94a3b8', marginLeft: 4 }}>{a.umd_nm}·{a.cnt}</span>
            </button>
          );
        })}
      </div>

      {loading && <div style={{ padding: 20, color: '#3b82f6' }}>조회 중…</div>}

      {/* ── 단지 정보 카드 ── */}
      {detail && (
        <div style={card}>
          <h3 style={{ margin: '0 0 10px', fontSize: 17 }}>
            {detail.apt_nm}
            <span style={{ fontSize: 12, fontWeight: 400, color: '#64748b', marginLeft: 8 }}>
              {detail.sgg_nm} {detail.umd_nm} {detail.jibun}
              {detail.road_addr && ` · ${detail.road_addr}`}
            </span>
          </h3>

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <Stat label="전체 세대수"
                  value={detail.hhld_cnt ? `${detail.hhld_cnt.toLocaleString()}세대` : '정보없음'} />
            <Stat label="준공" value={detail.build_year || '-'} />
            <Stat label="대지면적"
                  value={detail.plat_area
                    ? `${Math.round(detail.plat_area).toLocaleString()}㎡`
                    : '-'} />
            <Stat label="건폐율"
                  value={detail.bc_rat != null ? `${detail.bc_rat}%` : '-'} />
            <Stat label="용적률"
                  value={detail.vl_rat != null ? `${detail.vl_rat}%` : '-'} />
            <Stat label="기간 내 거래" value={`${detail.period_cnt}건`} />
            <Stat label="세대수 대비 매매율"
                  value={detail.turnover_pct != null ? `${detail.turnover_pct}%` : '-'}
                  highlight={detail.turnover_pct != null && detail.turnover_pct >= 5} />
            <Stat label="평균 거래가" value={fmtAmt(detail.avg_amt)} />
            <Stat label="평균 평당가"
                  value={detail.avg_unit ? `${detail.avg_unit.toLocaleString()}만` : '-'} />
          </div>

          {detail.turnover_pct != null && (
            <p style={{ margin: '10px 0 0', fontSize: 11, color: '#94a3b8' }}>
              ※ 매매율 = 기간 내 거래건수 ÷ 전체 세대수. 높을수록 손바뀜이 활발합니다.
              신축 입주장에서는 자연히 높게 나옵니다.
            </p>
          )}
        </div>
      )}

      {/* ── 지도 ── */}
      {/* 좌표가 없는 단지는 안내만 표시 (지도 영역이 통째로 사라지면 원인을 알 수 없다) */}
      {detail && !detail.lat && (
        <div style={{ ...card, background: '#fffbeb', border: '1px solid #fde68a' }}>
          <div style={{ fontSize: 13, color: '#92400e' }}>
            📍 이 단지는 아직 위치 정보가 수집되지 않았습니다.
            <span style={{ color: '#a16207', marginLeft: 6 }}>
              (거래 20건 이상 단지부터 순차 수집 중 · 현재 {detail.trade_cnt}건)
            </span>
          </div>
        </div>
      )}

      {detail?.lat && (
        <div style={card}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
            <h4 style={{ margin: 0, fontSize: 14 }}>위치</h4>
            {/* 토글 ON 이면 조회 후 지도에 표시, OFF 면 마커·반경원을 지운다 */}
            <label style={{ marginLeft:'auto', display:'flex', alignItems:'center',
                            gap:8, cursor:'pointer', fontSize:13 }}>
              <span style={{ color:'#475569' }}>🏢 주변 중개사무소</span>
              <span onClick={() => {
                      const next = !showAgents;
                      setShowAgents(next);
                      if (next && !agents) loadAgents();
                      if (!next) setAgents(null);
                    }}
                    style={{
                      width:44, height:24, borderRadius:12, position:'relative',
                      background: showAgents ? '#3b82f6' : '#cbd5e1',
                      transition:'background .2s', display:'inline-block',
                    }}>
                <span style={{
                  position:'absolute', top:3, left: showAgents ? 23 : 3,
                  width:18, height:18, borderRadius:9, background:'#fff',
                  transition:'left .2s',
                }} />
              </span>
            </label>
          </div>

          <div ref={mapDivRef} style={{ width: '100%', height: 380, borderRadius: 6 }} />

          {agents && (
            <div style={{ marginTop: 10, fontSize: 13 }}>
              반경 <b>{agents.radius}m</b> 내 중개사무소 <b style={{ color: '#dc2626' }}>{agents.count}</b>곳
              {detail.hhld_cnt > 0 && agents.count > 0 && (
                <span style={{ marginLeft: 10, color: '#475569' }}>
                  · 중개사무소 1곳당 <b>{Math.round(detail.hhld_cnt / agents.count).toLocaleString()}</b>세대
                </span>
              )}
              {agents.count === 0 && (
                <span style={{ marginLeft: 8, color: '#94a3b8' }}>
                  (중개사무소 데이터가 아직 수집되지 않았습니다)
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── 가격 추이 그래프 ── */}
      {series.length > 0 && (
        <>
          <div style={card}>
            <h4 style={{ margin: '0 0 8px', fontSize: 14 }}>
              면적 타입별 {metric === 'avg' ? '거래금액' : '평당가'} 추이
            </h4>
            <ResponsiveContainer width="100%" height={320}>
              <LineChart data={chartData} margin={{ top: 10, right: 20, bottom: 5, left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="ym" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 11 }} width={55}
                       tickFormatter={v => (v/10000).toFixed(v>=10000?1:0) + '억'} />
                <Tooltip formatter={(v, n) => [
                  metric === 'avg' ? fmtAmt(v) : v.toLocaleString() + '만원/평', n]}
                  labelFormatter={l => `20${l.replace('.', '년 ')}월`} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {series.map((s, i) => (
                  <Line key={s.area_type} type="monotone" dataKey={`${s.area_type}㎡`}
                        stroke={COLORS[i % COLORS.length]} strokeWidth={2}
                        dot={{ r: 3 }} connectNulls />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div style={card}>
            <h4 style={{ margin: '0 0 10px', fontSize: 14 }}>면적 타입별 상세</h4>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f1f5f9' }}>
                  {['전용면적','평형','거래건수','최근 평균','최고가','최저가'].map(h =>
                    <th key={h} style={th}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {series.map((s, i) => {
                  const last = s.points[s.points.length - 1];
                  return (
                    <tr key={s.area_type} style={{ borderTop: '1px solid #f1f5f9' }}>
                      <td style={td}>
                        <span style={{ display:'inline-block', width:10, height:10,
                          borderRadius:2, marginRight:6,
                          background: COLORS[i % COLORS.length] }} />
                        {s.ar_min === s.ar_max ? `${s.ar_min}㎡` : `${s.ar_min}~${s.ar_max}㎡`}
                      </td>
                      <td style={tdR}>{s.pyeong}평</td>
                      <td style={tdR}>{s.total_cnt}건</td>
                      <td style={tdR}>{last ? fmtAmt(last.avg) : '-'}</td>
                      <td style={{ ...tdR, color:'#dc2626' }}>
                        {fmtAmt(Math.max(...s.points.map(p => p.max)))}</td>
                      <td style={{ ...tdR, color:'#2563eb' }}>
                        {fmtAmt(Math.min(...s.points.map(p => p.min)))}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* [RT_APTVOL_V1] 평형별 월별 거래건수 — 선택 단지 (lawd_cd·동·지번) */}
          <AptBucketVolume lawdCd={detail?.lawd_cd || lawdCd} umdNm={detail?.umd_nm}
                           jibun={detail ? (detail.jibun ?? '') : undefined}
                           from={from} to={to} propType={propType} />
          {/* ── 개별 거래내역 ── */}
          <div style={card}>
            <div style={{ display:'flex', alignItems:'center', gap:10,
                          flexWrap:'wrap', marginBottom:10 }}>
              <h4 style={{ margin:0, fontSize:14 }}>거래 내역</h4>

              {/* 평형별 필터 - series 에서 면적 타입 목록을 가져온다 */}
              <select value={dealArea} onChange={e => setDealArea(e.target.value)}
                      style={{ padding:'5px 8px', border:'1px solid #cbd5e1',
                               borderRadius:4, fontSize:13 }}>
                <option value="">전체 평형</option>
                {series.map(s => (
                  <option key={s.area_type} value={s.area_type}>
                    {s.ar_min === s.ar_max ? `${s.ar_min}` : `${s.ar_min}~${s.ar_max}`}㎡
                    ({s.pyeong}평) · {s.total_cnt}건
                  </option>
                ))}
              </select>

              <span style={{ fontSize:13, color:'#475569' }}>
                {dealArea
                  ? deals.filter(x => x.area_type === Number(dealArea)).length
                  : deals.length}건
              </span>
            </div>

            <div style={{ maxHeight:420, overflowY:'auto',
                          border:'1px solid #e2e8f0', borderRadius:6 }}>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
                <thead style={{ position:'sticky', top:0, background:'#f1f5f9' }}>
                  <tr>
                    {['거래일','전용면적','층','거래금액','평당가','거래구분']
                      .map(h => <th key={h} style={th}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {deals
                    .filter(x => !dealArea || x.area_type === Number(dealArea))
                    .map(r => (
                      <tr key={r.id} style={{ borderTop:'1px solid #f1f5f9' }}>
                        <td style={td}>{r.deal_date}</td>
                        <td style={tdR}>
                          {Number(r.exclu_use_ar).toFixed(1)}㎡
                          <span style={{ color:'#94a3b8', marginLeft:4 }}>
                            ({(r.exclu_use_ar / 3.305785).toFixed(1)}평)
                          </span>
                        </td>
                        <td style={tdR}>{r.floor_no}층</td>
                        <td style={{ ...tdR, fontWeight:600, color:'#1e40af' }}>
                          {fmtAmt(r.deal_amount)}
                        </td>
                        <td style={tdR}>{r.unit_price?.toLocaleString()}</td>
                        <td style={td}>
                          <span style={{ fontSize:11, padding:'2px 6px', borderRadius:3,
                            background: r.dealing_gbn === '직거래' ? '#fef3c7' : '#dbeafe',
                            color: r.dealing_gbn === '직거래' ? '#92400e' : '#1e40af' }}>
                            {r.dealing_gbn || '-'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  {deals.length === 0 && (
                    <tr><td colSpan={6} style={{ ...td, textAlign:'center',
                      padding:20, color:'#94a3b8' }}>거래 내역이 없습니다</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <p style={{ margin:'8px 0 0', fontSize:11, color:'#94a3b8' }}>
              ※ 최대 500건까지 표시됩니다. 기간을 좁히면 전체를 볼 수 있습니다.
            </p>
          </div>
          
        </>
      )}
    </div>
  );
}

// 통계 박스 하나
function Stat({ label, value, highlight }) {
  return (
    <div style={{ flex: '1 1 130px', minWidth: 0, padding: '10px 12px',
                  background: highlight ? '#fef2f2' : '#f8fafc', borderRadius: 6 }}>
      <div style={{ fontSize: 11, color: '#64748b', marginBottom: 3 }}>{label}</div>
      <div style={{ fontSize: 16, fontWeight: 700,
                    color: highlight ? '#dc2626' : '#1e293b' }}>{value}</div>
    </div>
  );
}

const th  = { padding: '8px', textAlign: 'left', fontWeight: 600, color: '#475569' };
const td  = { padding: '8px' };
const tdR = { ...td, textAlign: 'right' };
const tabOn  = { padding: '6px 14px', fontSize: 12, cursor: 'pointer', borderRadius: 4,
                 border: '1px solid #3b82f6', background: '#3b82f6', color: '#fff' };
const tabOff = { padding: '6px 14px', fontSize: 12, cursor: 'pointer', borderRadius: 4,
                 border: '1px solid #cbd5e1', background: '#fff', color: '#475569' };